// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TopNavMenu.tsx
 * @input Uses React, StyleX, usePopover, useMenuHover, useListFocus,
 *   useTypeahead, TopNavItem tokens
 * @output Exports TopNavMenu component and related types
 * @position Navigation item with hover-triggered overflow menu for TopNav
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TopNav/TopNav.doc.mjs
 * - /packages/core/src/TopNav/TopNavMenu.test.tsx
 * - /packages/core/src/TopNav/index.ts
 * - /packages/cli/assets/templates/blocks/components/TopNav/ (showcase blocks)
 */
import React, { useCallback, useId, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { usePopover } from "../Popover/usePopover.js";
import { useMenuHover } from "../hooks/useMenuHover.js";
import { useListFocus } from "../hooks/useListFocus.js";
import { useTypeahead } from "../hooks/useTypeahead.js";
import { useIcon } from "../Icon/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { useTopNavSlot } from "./TopNavContext.js";
import { useTopNavRenderMode } from "./TopNavRenderContext.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { themeProps } from "../utils/themeProps.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, fontWeightVars, typeScaleVars } from "../theme/tokens.stylex.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  menuOffset: {
    keoZOQ: "xcsaf9d",
    $$css: true
  }
};
const drawerStyles = {
  header: {
    kjj79g: "x1qughib",
    $$css: true
  },
  item: {
    kZCmMZ: "x31w388",
    kybGjl: "x1hl2dhg",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

/**
 * An item in the TopNav overflow menu.
 */

function getMenuItemKey(item) {
  return item.title;
}
// =============================================================================
// TopNavMenu
// =============================================================================

/**
 * A navigation item that displays a hover-triggered overflow menu.
 *
 * Renders as a nav item in TopNav's startContent slot. On hover,
 * shows a popover with rich menu items containing an icon, title,
 * and optional description.
 *
 * @example
 * ```
 * <TopNav
 *   startContent={
 *     <>
 *       <TopNavItem label="Home" href="/" isSelected />
 *       <TopNavMenu
 *         label="Products"
 *         items={[
 *           {
 *             title: 'Analytics',
 *             description: 'Track and analyze user behavior',
 *             icon: <ChartBarIcon />,
 *             href: '/products/analytics',
 *           },
 *           {
 *             title: 'Messaging',
 *             description: 'Real-time communication tools',
 *             icon: <ChatBubbleIcon />,
 *             href: '/products/messaging',
 *           },
 *         ]}
 *       />
 *     </>
 *   }
 * />
 * ```
 */

export function TopNavMenu({
  ref,
  label,
  items,
  delay = 150,
  hideDelay = 200
}) {
  const renderMode = useTopNavRenderMode();
  const chevronDownIcon = useIcon('chevronDown');
  const {
    closeMobileNav
  } = useAppShellMobile();
  const LinkComponent = useLinkComponent();
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const menuId = useId();
  const slot = useTopNavSlot();
  const triggerButtonRef = useRef(null);
  const popover = usePopover({
    // The popup's own role="menu" is the exposed semantics; a modal dialog
    // wrapper would announce an unnamed dialog around the menu and make the
    // trigger claim aria-haspopup="dialog" for menu content (see TabMenu).
    role: 'none',
    xstyle: styles.menuOffset
  });
  const {
    triggerProps,
    contentProps,
    menuRef,
    setTriggerEl
  } = useMenuHover({
    show: popover.show,
    hide: popover.hide,
    isOpen: popover.isOpen,
    isEnabled: true,
    showDelay: delay,
    hideDelay
  });
  const setTriggerRef = mergeRefs(triggerButtonRef, popover.triggerRef, setTriggerEl, ref);

  // The desktop popup is a composite menu widget per the APG menu pattern:
  // a single roving tab stop with ArrowUp/ArrowDown traversal (wrapping),
  // Home/End, and first-character typeahead. The hook owns item tabindex —
  // items render tabIndex={-1} and exactly one is promoted to 0. The
  // composition mirrors NavHeadingMenu.
  const {
    listRef,
    handleKeyDown,
    handleFocus,
    focusItem
  } = useListFocus({
    itemSelector: '[role="menuitem"]',
    hasRovingTabIndex: true,
    onEscape: popover.hide
  });

  // First-character typeahead over the menu items (menus-11).
  const getMenuItems = useCallback(() => listRef.current ? Array.from(listRef.current.querySelectorAll('[role="menuitem"]')) : [], [listRef]);
  const typeahead = useTypeahead({
    getItemLabels: () => getMenuItems().map(el => el.textContent),
    onMatch: focusItem,
    getCurrentIndex: () => getMenuItems().findIndex(el => el === document.activeElement || el.contains(document.activeElement))
  });

  // Extend useListFocus with Enter/Space activation. Items rendered without an
  // `href` are `<div role="menuitem">` elements, which have no native keyboard
  // activation — without this, Enter/Space on a focused onClick-only item does
  // nothing. Anchor items (with `href`) already activate on Enter natively.
  const menuKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      const focused = document.activeElement;
      if (focused?.getAttribute('role') === 'menuitem') {
        e.preventDefault();
        focused.click();
        return;
      }
    }
    if (typeahead.onKeyDown(e)) {
      e.preventDefault();
      return;
    }
    handleKeyDown(e);
  }, [handleKeyDown, typeahead]);

  // Menu container carries both the hover hook's ref (for its open/close
  // focus management) and the list-focus ref (for roving tabindex/typeahead).
  const setMenuRef = mergeRefs(menuRef, listRef);

  // Mobile bar: hide menus entirely
  if (renderMode === 'mobile-bar') {
    return null;
  }

  // Drawer mode: collapsible section
  if (renderMode === 'drawer') {
    return /*#__PURE__*/_jsxs("div", {
      ...{
        className: "x78zum5 xdt5ytf"
      },
      children: [/*#__PURE__*/_jsxs("button", {
        type: "button",
        onClick: () => setDrawerExpanded(v => !v),
        "aria-expanded": drawerExpanded,
        "aria-controls": `${menuId}-items`,
        ...stylex.props(navItemStyles.item, drawerStyles.header),
        children: [label, /*#__PURE__*/_jsx("span", {
          ...{
            0: {
              className: "x3nfvp2 x11xpdln xuedmi6 xlr8y92"
            },
            1: {
              className: "x3nfvp2 x11xpdln xuedmi6 xlr8y92 x19jd1h0"
            }
          }[!!drawerExpanded << 0],
          children: chevronDownIcon
        })]
      }), /*#__PURE__*/_jsx("div", {
        id: `${menuId}-items`,
        ...{
          0: {
            className: "xrvj5dj xihq33y x1qn9uv2 x80gvsz xlr8y92"
          },
          1: {
            className: "xrvj5dj x1qn9uv2 x80gvsz xlr8y92 x1tu4anv"
          }
        }[!!drawerExpanded << 0],
        children: /*#__PURE__*/_jsx("div", {
          ...{
            className: "xb3r6kr x2lwn1j"
          },
          children: items.map(item => /*#__PURE__*/_jsxs(LinkComponent, {
            href: item.href,
            onClick: _e => {
              item.onClick?.();
              closeMobileNav();
            },
            ...stylex.props(navItemStyles.item, drawerStyles.item),
            children: [item.icon && /*#__PURE__*/_jsx("span", {
              ...{
                className: "x2lah0s xw4jnvo x1qx5ct2"
              },
              children: item.icon
            }), /*#__PURE__*/_jsxs("span", {
              ...{
                className: "x78zum5 xdt5ytf x1lsbc85"
              },
              children: [item.title, item.description && /*#__PURE__*/_jsx("span", {
                ...{
                  className: "x141an7d xv1l7n4 x1sodnla"
                },
                children: item.description
              })]
            })]
          }, getMenuItemKey(item)))
        })
      })]
    });
  }

  // Default: desktop popover
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("button", {
      ref: setTriggerRef,
      type: "button",
      ...popover.triggerProps,
      ...triggerProps,
      ...mergeProps(themeProps('top-nav-menu'), {
        0: {
          className: "x3nfvp2 x6s0dn4 x1txdalj x1vofgu7 xrrkdod xh6dtrn xcr08ib x1kq96og x1e4wzip xv1l7n4 x1hl2dhg x1ypdohk xs2xxs2 xuedmi6 xlr8y92 xjbqb8w xe9uy6x x17nn4n9 x1wfwxd8 x7s97pk xjb2p0i"
        },
        1: {
          className: "x3nfvp2 x6s0dn4 x1txdalj x1vofgu7 xrrkdod xh6dtrn xcr08ib x1kq96og x1e4wzip x1hl2dhg x1ypdohk xs2xxs2 xuedmi6 xlr8y92 x17nn4n9 x1wfwxd8 x7s97pk xjb2p0i x1tgivj0 x1lmrjuc"
        }
      }[!!popover.isOpen << 0]),
      children: [label, /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x3nfvp2 x6s0dn4 x11xpdln xuedmi6 xlr8y92"
          },
          1: {
            className: "x3nfvp2 x6s0dn4 x11xpdln xuedmi6 xlr8y92 x19jd1h0"
          }
        }[!!popover.isOpen << 0],
        children: chevronDownIcon
      })]
    }), popover.render(/*#__PURE__*/_jsx("div", {
      ref: setMenuRef,
      role: "menu",
      "aria-label": label,
      ...contentProps,
      // After the contentProps spread so the full APG composition (roving
      // tabindex + typeahead + Enter/Space activation) replaces the hover
      // hook's basic arrow-key handler.
      onKeyDown: menuKeyDown,
      onFocus: handleFocus,
      ...{
        className: "x78zum5 xdt5ytf xzye2dw x1u2d2a2 x9epnlk"
      },
      children: items.map(item => {
        const Element = item.href ? 'a' : 'div';
        return /*#__PURE__*/_jsxs(Element, {
          role: "menuitem"
          // Single tab stop: useListFocus owns the roving tabindex and
          // promotes exactly one item to 0.
          ,
          tabIndex: -1,
          href: item.href,
          onClick: item.onClick,
          ...{
            className: "x78zum5 x6s0dn4 xjcht0a x8o8v82 xrrkdod xh6dtrn x1hl2dhg x1ypdohk x15406qy xuedmi6 xlr8y92 xjbqb8w xe9uy6x x17nn4n9 x1wfwxd8 x7s97pk"
          },
          children: [/*#__PURE__*/_jsx("div", {
            ...{
              className: "x78zum5 x6s0dn4 xl56j7k x100vrsf x1vqgdyp xh6dtrn x17x4s8c x2lah0s"
            },
            children: item.icon
          }), /*#__PURE__*/_jsxs("div", {
            ...{
              className: "x78zum5 xdt5ytf xzye2dw xeuugli"
            },
            children: [/*#__PURE__*/_jsx("span", {
              ...{
                className: "xcr08ib x1kq96og x2mo6ok x1tgivj0"
              },
              children: item.title
            }), item.description && /*#__PURE__*/_jsx("span", {
              ...{
                className: "x141an7d x1ltkj2j x1sodnla xv1l7n4"
              },
              children: item.description
            })]
          })]
        }, getMenuItemKey(item));
      })
    }), {
      placement: 'below',
      alignment: slot,
      xstyle: styles.menuOffset
    })]
  });
}
TopNavMenu.displayName = 'TopNavMenu';